import React from 'react';
import styled from '@emotion/styled';
import { useLocale } from 'locale';
import { SmallText } from 'ds/components/typography';
import { CheckboxField } from 'ds/components/input/CheckboxField';

export enum LegendType {
  Sale = 'sale',
  Rent = 'rent',
  Sold = 'sold',
}

export enum LegendStatus {
  Active,
  Inactive,
  Disabled,
}

export type LegendState = Record<LegendType, LegendStatus>;

const statusToCircleColorMap = {
  [LegendType.Sale]: '#DE6992',
  [LegendType.Rent]: '#FFC95E',
  [LegendType.Sold]: '#767676',
};

const Wrapper = styled.div`
  ${({ theme }) => `
    box-shadow: ${theme.shadow.level5};
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.neutrals.white};
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
    z-index: 1;
    position: absolute;
    border: 1px solid ${theme.colors.neutrals.grey8};
  `}
  ${({ theme }) => theme.media.showAt({ from: 3 })`
    left: 24px;
    top: 24px;
  `}
  ${({ theme }) => theme.media.showAt({ to: 2 })`
    right: 16px;
    top: 16px;
  `}
`;
const Item = styled.div`
  ${({ theme }) => `
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    &:not(:last-of-type) {
      margin-bottom: ${theme.spacing(1)};
    }
  `}
`;
const Circle = styled.div<{ legendType: LegendType }>`
${({ theme, legendType }) => `
    height: 10px;
    width: 10px;
    background-color: ${statusToCircleColorMap[legendType]};
    border-radius: ${theme.borderRadius.round};
    margin-right: ${theme.spacing(1)};
  `}
`;

interface ILegendProps {
  legendState: LegendState;
  setLegendState: React.Dispatch<LegendState>;
}

const statusSwitcher = {
  [LegendStatus.Active]: LegendStatus.Inactive,
  [LegendStatus.Inactive]: LegendStatus.Active,
};

const Legend: React.FC<ILegendProps> = ({ legendState, setLegendState }) => {
  const { t } = useLocale();

  const handleLegendClick = (legendType: LegendType) => {
    setLegendState({ ...legendState, [legendType]: statusSwitcher[legendState[legendType]] });
  };

  return (
    <Wrapper>
      {(Object.keys(legendState) as LegendType[]).map(legendType => {
        if (legendState[legendType] === LegendStatus.Disabled) return null;

        return (
          <Item key={legendType}>
            <CheckboxField checked={legendState[legendType] === LegendStatus.Active} onClick={() => handleLegendClick(legendType)} />
            <SmallText onClick={() => handleLegendClick(legendType)}>{t('agentOffice.map.legend', { legendType } )}</SmallText>
            <Circle legendType={legendType} onClick={() => handleLegendClick(legendType)} />
          </Item>
        );
      })}
    </Wrapper>
  );
};

export default Legend;
